import React, {useEffect, useMemo, useState} from 'react';
import {Autocomplete, Button, Checkbox, FormControlLabel, Grid, TextField, Tooltip, Typography} from "@mui/material";
import {getAllLocationNames} from "../../components/location/requests/location-requests";
import useGetRequestBody from "../../hooks/useGetRequestBody";
import {customNumberField, DAILY, ONE_TIME, getRentalAgreementURL} from "../../util/BookingUtil";
import SimpleSelect from "../../components/SimpleSelect";
import {formatCurrencyValue, parseCurrencyValue, validateCurrencyValue} from "../../util/PaymentUtils";
import moment from "moment";
import BrokerBookingCostBreakDown from "./BrokerBookingCostBreakDown";
import {getAllBuyerAccounts} from "../../components/user/request/user-requests";
import {bookSpace, calculateBookingChargeAmount} from "../requests/booking-requests";
import {DateFormats} from "../../components/constants/securspace-constants";
import {withSnackbar} from "../../components/hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../util/NetworkErrorUtil";
import {useNavigate, Link} from "react-router";
import {sendPurchaseEvent} from "../../util/GoogleTagManagerUtil";
import {toast} from "react-toastify";
import BookingFromToDatePicker from "../../components/BookingFromToDatePicker";
import SimplePercentageField from "../../components/SimplePercentageField";
import {bankersRound} from '../../util/MoneyUtil';
import {SecurSpaceReportContainer} from "../../components/common";

const BrokerBooking = ({account, snackbarShowMessage}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (brokeredBooking) => {
    if (loading) return;
    setLoading(true);
    const response = await bookSpace(brokeredBooking)
      .catch((err) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err, 'Error creating brokered booking'), 'error');
        setLoading(false);
      });
    if (!response) return;
    const {body} = response;
    sendPurchaseEvent(body);
    if (body?.orderNumber) {
      // Persist the message to the supplier bookings page.
      toast.success(`Created brokered booking ${body.orderNumber}`);
      navigate(`/bookings/supplier-bookings?${new URLSearchParams({
        bookingNumber: body.orderNumber
      })}`);
    } else {
      toast.error('Error creating booking: no order number');
      navigate('/bookings/supplier-bookings');
    }
    setLoading(false);
  }

  return (
    <SecurSpaceReportContainer container>
      <Grid item xs={12}><Typography variant={'h5'} component={'h1'}>New Brokered Booking</Typography></Grid>
      <BrokerBookingFrom account={account} onSubmit={handleSubmit} snackbarShowMessage={snackbarShowMessage}/>
    </SecurSpaceReportContainer>
  );
};

const BrokerBookingFrom = ({account, onSubmit, snackbarShowMessage}) => {
  const supplierLocations = useGetRequestBody(getAllLocationNames, useMemo(() => [], []));
  const buyers = useGetRequestBody(getAllBuyerAccounts, useMemo(() => [], []));
  const [loading, setLoading] = useState(false);
  const [brokeredBooking, setBrokeredBooking] = useState({
    assetType: '',
    brokeredBooking: true,
    brokeredBuyerChargedPerOccurrence: 0,
    brokeredSupplierPaidPerOccurrence: 0,
    buyerAccountId: null,
    chargeOverages: false,
    durationType: DAILY,
    endDate: moment().add(1, 'days').format(DateFormats.DAY),
    frequency: ONE_TIME,
    ignoreDate: true,
    initialCharge: undefined,
    locationId: null,
    manualPayment: false,
    numberOfSpaces: 1,
    paymentMethodId: null,
    rate: undefined, // can probably remove this too
    recurrences: 0,
    recurringCharge: 0,
    source: "MARKETPLACE",
    startDate: moment().format(DateFormats.DAY),
    supplierAccountId: account.id,
    tosDocUrl: '',
  });

  const defaultSsFeePercentage = account?.feePercentage || 17;
  const [costData, setCostData] = useState();
  const [buyerAmountPerSpace, setBuyerAmountPerSpace] = useState(0);
  const [supplierAmountPerSpace, setSupplierAmountPerSpace] = useState(0);
  const [securSpaceFeePercentage, setSecurSpaceFeePercentage] = useState(defaultSsFeePercentage);
  const [submitDisabled, setSubmitDisabled] = useState(false);

  const perSpaceFieldNames = {buyer: 'buyerAmountPerSpace', supplier: 'supplierAmountPerSpace', ssFee: 'securSpaceFee'};

  const getSupplierPerDay = (buyerPerDay, ssPercentage) => {
    return bankersRound(buyerPerDay * (100 - ssPercentage) / 100)
  }

  const getSSFeePercentage = (buyerPerDay, supplierPerDay) => {
    let percentage = defaultSsFeePercentage;
    if (buyerPerDay !== 0 && supplierPerDay !== 0) {
      percentage = (1 - supplierPerDay/buyerPerDay) * 100;
    }
    return percentage;
  }

  const supplierAmount = getSupplierPerDay(brokeredBooking.brokeredBuyerChargedPerOccurrence, securSpaceFeePercentage);
  const securSpaceFeeAmount = brokeredBooking.brokeredBuyerChargedPerOccurrence * securSpaceFeePercentage/ 100;

  const getBookingChargeAmount = async (booking) => {
    setLoading(true);
    const {
      buyerAccountId, locationId, startDate, endDate, frequency,
      recurrences, durationType, rate, assetType, chargeOverages, brokeredBuyerChargedPerOccurrence,
      brokeredSupplierPaidPerOccurrence,
    } = booking;

    if (!buyerAccountId || !locationId || !assetType || !booking.numberOfSpaces) {
      setLoading(false);
      return;
    }

    const numberOfSpaces = Number.parseInt(booking.numberOfSpaces) || 0;

    const costObject = {
      supplierAccountId: account.id,
      buyerAccountId,
      locationId,
      numberOfSpaces,
      startDate: moment(startDate).format(DateFormats.DAY),
      endDate: moment(endDate).format(DateFormats.DAY),
      frequency,
      recurrences,
      durationType,
      rate,
      assetType,
      brokeredBooking: true,
      chargeOverages,
      brokeredBuyerChargedPerOccurrence,
      brokeredSupplierPaidPerOccurrence,
      ignoreDate: true,
    };

    const response = await calculateBookingChargeAmount(costObject)
      .catch((err) => {
          snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err, 'Error calculating cost'), 'error');
          setSubmitDisabled(false);
        }
      );
    setCostData(response.body);
    setLoading(false);
  };

  const updatePerSpaceItems = (name, amount) => {
    let buyer = parseCurrencyValue(buyerAmountPerSpace);
    let supplier = parseCurrencyValue(supplierAmountPerSpace);
    let ssFeeAmount = 0;

    let ssFeePercentage = securSpaceFeePercentage;

    switch(name) {
      case perSpaceFieldNames.ssFee:
        ssFeePercentage = amount;
        ssFeeAmount = amount/100 * buyer;
        supplier = buyer - ssFeeAmount;
        break;
      case perSpaceFieldNames.buyer:
        buyer = amount;
        supplier = getSupplierPerDay(buyer, ssFeePercentage);
        ssFeeAmount = buyer - supplier;
        break;
      case perSpaceFieldNames.supplier:
        supplier = amount;
        ssFeeAmount = buyer - supplier;
        ssFeePercentage = getSSFeePercentage(buyer, supplier);
        break;
    }

    if(supplier > buyer) {
      snackbarShowMessage('Supplier amount cannot be greater than buyer amount', 'error');
    }
    return {buyer: buyer, supplier: supplier, ssFeePercentage: ssFeePercentage};
  }

  const handleChange = (event, parsedValue) => {
    const {name, value} = event.target;

    const perSpaceItems = updatePerSpaceItems(name, parseCurrencyValue(value));

    setSecurSpaceFeePercentage(perSpaceItems.ssFeePercentage);
    setSupplierAmountPerSpace(perSpaceItems.supplier);
    setBuyerAmountPerSpace(perSpaceItems.buyer);

    if(name === perSpaceFieldNames.ssFee) {
      const supplierPercentagePaid = (100 - parsedValue) / 100;
      setBrokeredBooking((prev) => {
        const updated = {
          ...prev,
          brokeredSupplierPaidPerOccurrence: brokeredBooking.brokeredBuyerChargedPerOccurrence * supplierPercentagePaid
        };
        getBookingChargeAmount(updated);
        return updated;
      });
    }
    if (name === perSpaceFieldNames.buyer) {
      if (!validateCurrencyValue(value)) return;
      setBrokeredBooking((prev) => {
        const buyerTotalAmount = parseCurrencyValue(value) * Number.parseInt(prev.numberOfSpaces);

        const updated = {
          ...prev,
          brokeredBuyerChargedPerOccurrence: buyerTotalAmount,
          brokeredSupplierPaidPerOccurrence: buyerTotalAmount * (100 - securSpaceFeePercentage) / 100,
        }
        getBookingChargeAmount(updated);
        return updated;
      });
      return;
    }
    if (name === perSpaceFieldNames.supplier) {
      if (!validateCurrencyValue(value)) return;
      setBrokeredBooking((prev) => {
        const supplierTotalAmount = parseCurrencyValue(value) * Number.parseInt(prev.numberOfSpaces);
        const updated = {
          ...prev,
          brokeredSupplierPaidPerOccurrence: supplierTotalAmount,
        }
        getBookingChargeAmount(updated);
        return updated;
      });
      return;
    }

    if (['numberOfSpaces'].includes(name)) {
      const numberOfSpaces = customNumberField(value);
      if (numberOfSpaces < 0) return;
      setBrokeredBooking((prev) => {
        const buyerTotalAmount = parseCurrencyValue(buyerAmountPerSpace) * numberOfSpaces
        const updated = {
          ...prev,
          brokeredBuyerChargedPerOccurrence: buyerTotalAmount,
          brokeredSupplierPaidPerOccurrence: buyerTotalAmount * (100 - securSpaceFeePercentage) / 100,
          numberOfSpaces
        };
        getBookingChargeAmount(updated);
        return updated;
      });
      return;
    } else {
      setBrokeredBooking((prev) => {
        const updated = {...prev, [name]: value};
        getBookingChargeAmount(updated);
        return updated;
      });
    }
  };

  const handleAutocompleteChange = (event, value, name) => {
    setBrokeredBooking((prev) => {
      const updated = {...prev, [name]: value?.id};
      getBookingChargeAmount(updated);
      return updated;
    });
  };

  const handleDateChange = (value, name) => {
    setBrokeredBooking((prev) => {
      const updated = {
        ...prev,
        [name]: value.format(DateFormats.DAY),
      };
      getBookingChargeAmount(updated);
      return updated;
    });
  };

  const handleBrokeredChange = (event) => {
    const {name, checked} = event.target;
    setBrokeredBooking((prev) => {
      const updated = {
        ...prev,
        [name]: checked,
      };
      getBookingChargeAmount(updated);
      return updated;
    });
  };

  const formId = 'broker-booking-form';

  const handleSubmit = (event) => {
    event.preventDefault();
    const submit = async () => {
      // Rate is overwritten in the backend so keep it undefined for now
      // const response = await getLocationById(brokeredBooking.locationId);
      // if (response.error) {
      //   snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(response.error, 'Error Submitting Booking'), 'error');
      //   return;
      // }
      // const location = response.body;
      onSubmit({
        ...brokeredBooking,
        initialCharge: costData.initialBookingChargeAmount,
        duration: costData.duration,
        tosDocUrl: getRentalAgreementURL(),
        // rate: calculateRate(brokeredBooking.assetType, location, false),
      });
    }
    submit();
  };

  useEffect(() => {
    if(!costData || supplierAmountPerSpace > buyerAmountPerSpace) {
      setSubmitDisabled(true)
    } else {
      setSubmitDisabled(false)
    }
  }, [buyerAmountPerSpace, supplierAmountPerSpace, costData])

  return (
    <Grid item container component={'form'} id={formId} onSubmit={handleSubmit} noValidate spacing={2}
          mt={.1}>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={brokeredBooking.locationId}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.label) return option.label;
              const optionId = option.id || option;
              const location = supplierLocations.find(loc => loc.id === optionId);
              return location.locationName || option;
            }}
            getOptionKey={(option) => option.id}
            onChange={(event, value) => handleAutocompleteChange(event, value, 'locationId')}
            renderInput={(params) => <TextField {...params} label={'Supplier Location'} variant={'standard'} fullWidth
                                                required/>}
            options={supplierLocations.map((location) => ({label: location.locationName, id: location.id}))}
            isOptionEqualToValue={(option, value) => option.id === value}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            value={brokeredBooking.buyerAccountId}
            getOptionLabel={(option) => {
              if (!option) return '';
              if (option.label) return option.label;
              const optionId = option.id || option;
              const buyer = buyers.find(buy => buy.id === optionId);
              return `${buyer.companyName} - ${buyer.email}` || option;
            }}
            onChange={(event, value) => handleAutocompleteChange(event, value, 'buyerAccountId')}
            renderInput={
              (params) => <TextField {...params} label={'Buyer Account'} variant={'standard'} fullWidth
                                     required/>}
            options={buyers.map((buyer) => ({label: `${buyer.companyName} - ${buyer.email}`, id: buyer.id}))}
            isOptionEqualToValue={(option) => option.id === brokeredBooking.buyerAccountId}
          />
        </Grid>
        <Grid item xs={12} md>
          <SimpleSelect
            id={'ss-broker-booking-select-service'}
            label={'Service'}
            name={'assetType'}
            value={brokeredBooking.assetType}
            onChange={handleChange}
            options={[
              {value: "Overage Spaces - Parking", label: "Overage Spaces - Parking"},
              {value: "Overage Spaces - Storage", label: "Overage Spaces - Storage"},
            ]}
            required
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} lg={8}>
          <BookingFromToDatePicker
            fromDate={brokeredBooking.startDate}
            onFromDateChange={(value) => handleDateChange(value, 'startDate')}
            onToDateChange={(value) => handleDateChange(value, 'endDate')}
            startEndLabel
            toDate={brokeredBooking.endDate}
            hideAutoRenewalNotice
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            id={'ss-broker-number-of-spaces'}
            label={'Spaces'}
            name='numberOfSpaces'
            onChange={handleChange} // TODO: prevent e, E, +, -, ., etc.
            type={'number'}
            placeholder='e.g. 4'
            required
            value={brokeredBooking.numberOfSpaces}
            InputProps={{inputMode: 'numeric'}}
            onWheel={(e) => e.target.blur()}
            variant='standard'
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} md>
          <TextField
            id={'ss-broker-booking-customer-amount'}
            name='customerAmount'
            value={formatCurrencyValue(brokeredBooking.brokeredBuyerChargedPerOccurrence, true)}
            label={'Customer Amount (Read-Only)'}
            helperText={'The subtotal amount charged to the customer before taxes and fees.'}
            fullWidth
            variant='standard'
            inputProps={{inputMode: 'numeric', readOnly: true}}
          />
        </Grid>
        <Grid item xs={12} md>
          <TextField
            id={'ss-broker-supplier-paid-amount'}
            name='supplierAmount'
            value={formatCurrencyValue(supplierAmount, true)}
            label={'Supplier Amount (Read-Only)'}
            helperText={"The portion of the buyer amount paid to the supplier after SecurSpace fees."}
            placeholder='e.g. $50.00'
            fullWidth
            variant='standard'
            inputProps={{inputMode: 'numeric', readOnly: true}}
          />
        </Grid>
        <Grid item xs={12} md>
          <SimplePercentageField
            id={'ss-brokered-booking-securspace-fee'}
            name='securSpaceFee'
            value={securSpaceFeePercentage}
            onChange={handleChange}
            label={'SecurSpace Fee %'}
            variant='standard'
            required
            fullWidth
            helperText={'The percentage of the buyer amount that SecurSpace takes as a fee.'}
            min={0}
            max={100}
          />
        </Grid>
      </Grid>
      <Grid item container spacing={2}>
        <Grid item xs={12} lg={4}>
          <TextField
            id={'ss-broker-buyer-amount-per-space'}
            inputProps={{inputMode: 'numeric'}}
            label={'Customer Amount Per Space'}
            name='buyerAmountPerSpace'
            onChange={handleChange}
            placeholder='e.g. $50.00'
            required
            value={formatCurrencyValue(buyerAmountPerSpace, true)}
            variant='standard'
            fullWidth
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <TextField
            id={'ss-broker-buyer-amount-per-space'}
            inputProps={{inputMode: 'numeric'}}
            label={'Supplier Amount Per Space'}
            name='supplierAmountPerSpace'
            onChange={handleChange}
            placeholder='e.g. $50.00'
            required
            value={formatCurrencyValue(supplierAmountPerSpace, true)}
            variant='standard'
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid item xs={12}><BrokerBookingCostBreakDown costData={costData} securSpaceFeeAmount={securSpaceFeeAmount}
                                                     loading={loading}/></Grid>
      <Grid item xs={12}>
        <Tooltip title={'When enabled, payment is collected outside the system. The booking will be created as pending rather than incomplete.'}>
          <FormControlLabel
            control={
              <Checkbox
                name={'manualPayment'}
                checked={brokeredBooking.manualPayment}
                onChange={handleBrokeredChange}
              />
            }
            label={'Manual Payment'}
          />
        </Tooltip>
      </Grid>
      <Buttons formId={formId} submitDisabled={submitDisabled || loading}/>
    </Grid>
  )
};

const Buttons = ({formId, submitDisabled}) => {
  return (
    <Grid item container spacing={2} justifyContent={'center'}>
      <Grid item xs={12} md={6}>
        <Button variant={'contained'} fullWidth type={'submit'} form={formId} disabled={submitDisabled}>Submit</Button>
      </Grid>
      <Grid item xs>
        <Button component={Link} to={'/bookings/supplier-bookings'} variant={'outlined'} fullWidth>Cancel</Button>
      </Grid>
    </Grid>
  )
};

export default withSnackbar(BrokerBooking);
